import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { StorageService } from 'src/app/core/services/storage.service';
import { Channel } from 'src/app/shared/models/channel.model';
import { Profile } from 'src/app/shared/models/profile.model';
import { User } from 'src/app/shared/models/user.model';
import { UserService } from 'src/app/shared/services/user.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService implements OnDestroy {
  public profile: Profile | undefined;

  public user: User | undefined;

  public hospitalId: any;

  public socket: any | undefined;

  public messagesSubject: BehaviorSubject<any> = new BehaviorSubject<any>([]);

  public messages: Observable<any> = this.messagesSubject.asObservable();

  public channelsSubject: BehaviorSubject<any> = new BehaviorSubject<any>([]);

  public channels: Observable<any> = this.channelsSubject.asObservable();

  public channelSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});

  public channel: Observable<any> = this.channelSubject.asObservable();

  public newInChannelSubject: Subject<any> = new Subject<any>();

  public newInSendBoxSubject: Subject<any> = new Subject<any>();

  public removedChannelSubject: Subject<any> = new Subject<any>();

  public removedChannelInChannelSubject: Subject<any> = new Subject<any>();

  public unseenMessagesSubject: Subject<any> = new Subject<any>();

  public seenByAllMessageSubject: Subject<any> = new Subject<any>();

  public isMessagingModeSubject: BehaviorSubject<any> = new BehaviorSubject<any>(false);

  public messageSeenNumSubject: BehaviorSubject<any> = new BehaviorSubject<any>(0);

  public inSendboxNewInChannelSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});

  public inSendboxNewChannelSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});

  public inGlobalNewChannelSubject: Subject<any> = new Subject<any>();

  public globalNewMessagesSubject: Subject<any> = new Subject<any>();

  constructor(
    private storageService: StorageService,
    private userService: UserService,
    private socketService: SocketService
  ) {
    const hospitals = this.userService.getSelectedHospitals();
    this.hospitalId = hospitals ? this.userService.getSelectedHospitals()[0] : undefined;
  }

  connect() {
    this.socket = this.socketService.getSocket();

    if (this.socket) {
      this.setupMessageListeners();
    } else {
      return;
    }
  }

  private setupMessageListeners(): void {
    if (!this.socket) {
      return;
    }

    this.getInChannelMessages();
    this.getInSendboxMessages();
    this.getGlobalNewMessages();
    this.getSeenByAllMessage();
    this.inSendboxNewInChannel();
    this.inGlobalNewChannel();
    this.inSendboxNewChannel();
    this.inChannelNewChannel();
    this.getUnseenMessages();
    this.inSendboxRemoveOfChannel();
    this.inChannelRemoveOfChannel();
  }

  connectToSandbox() {
    this.emitChannel(this.hospitalId);
  }

  disconnectToSandbox() {
    this.emitUnchannel(this.hospitalId);
  }

  joinChannel(channelId: string, oldChannelId?: string): void {
    if (oldChannelId) {
      this.emitUnchannel(oldChannelId);
    }
    this.emitChannel(channelId);
  }

  emitChannel(channelId: string) {
    if (this.socket) {
      this.socket.emit('channel', { channelId: channelId });
    }
  }

  emitUnchannel(channelId: string) {
    if (this.socket) {
      this.socket.emit('unchannel', { channelId: channelId });
    }
  }

  sendSeenMessages(messages: any) {
    if (this.socket) {
      this.socket.emit('messageSeen', messages);
    }
  }

  sendMessage(
    text: string,
    isOneToOne: boolean,
    firstName: string,
    lastName: string,
    channelId: string,
    channelName: string,
    messageNumber?: number
  ): void {
    const messageToSend = {
      message: text,
      isOneToOne: isOneToOne,
      channelId: channelId,
      messageNumber: messageNumber,
      firstName: firstName,
      lastName: lastName,
      channelName: channelName,
    };
    if (this.socket) {
      this.socket.emit('onMessageSend', messageToSend);
    }
  }

  getInChannelMessages() {
    if (this.socket) {
      this.socket.on('inChannelMessage', (data: any) => {
        if (data) {
          this.newInChannelSubject.next(data);
          this.sendSeenMessages([{ messageId: data.messageId }]);
        }
      });
    }
  }

  getInSendboxMessages() {
    if (this.socket) {
      this.socket.on('inSendboxMessage', (data: any) => {
        if (data) {
          this.newInSendBoxSubject.next(data);
        }
      });
    }
  }

  getGlobalNewMessages() {
    if (this.socket) {
      this.socket.on('inGlobalMessage', (messages: any) => {
        this.globalNewMessagesSubject.next(messages.messageNb);
      });
    }
  }

  getSeenByAllMessage() {
    if (this.socket) {
      this.socket.on('seenByAll', (message: any) => {
        this.seenByAllMessageSubject.next(message);
      });
    }
  }

  inSendboxNewChannel() {
    if (this.socket) {
      this.socket.on('inSendboxNewChannel', (data: any) => {
        if (data) {
          this.inSendboxNewChannelSubject.next(data);
        }
      });
    }
  }

  inGlobalNewChannel() {
    if (this.socket) {
      this.socket.on('inGlobalNewChannel', (data: any) => {
        if (data) {
          this.inGlobalNewChannelSubject.next(data);
        }
      });
    }
  }

  addChannel(profiles: string[], name: string, channelNumber: number): void {
    if (this.socket) {
      this.socket.emit('createChannel', {
        channel: {
          profileId: this.storageService.getUser().profile?._id,
          hospitalId: this.userService.getSelectedHospitals()[0],
          members: profiles || [],
          name: name || '',
        },
        profileId: this.storageService.getUser().profile?._id,
        hospitalId: this.userService.getSelectedHospitals()[0],
        channelNumber: channelNumber,
      });
    }
  }

  editChannel(channel: Channel): void {
    if (this.socket) {
      const newChannel = this.generateChannel(channel);
      this.socket.emit('editToChannel', {
        channelId: newChannel._id,
        newChannel: newChannel,
      });
    }
  }

  inSendboxRemoveOfChannel() {
    if (this.socket) {
      this.socket.on('inSendboxRemovedOfChannel', (data: any) => {
        if (data) {
          this.removedChannelSubject.next(data);
        }
      });
    }
  }

  inChannelRemoveOfChannel() {
    if (this.socket) {
      this.socket.on('inChannelRemovedOfChannel', (data: any) => {
        if (data) {
          this.removedChannelInChannelSubject.next(data);
        }
      });
    }
  }

  inSendboxNewInChannel() {
    if (this.socket) {
      this.socket.on('inSendboxNewInChannel', (data: any) => {
        if (data) {
          this.inSendboxNewInChannelSubject.next(data);
        }
      });
    }
  }

  inChannelNewChannel() {
    if (this.socket) {
      this.socket.on('inChannelNewChannel', (data: any) => {
        if (data) {
          this.inSendboxNewChannelSubject.next(data);
        }
      });
    }
  }

  getUnseenMessages() {
    if (this.socket) {
      this.socket.on('notSeenMessage', (messages: any) => {
        this.unseenMessagesSubject.next(messages.totalNotSeen);
      });
    }
  }

  generateChannel(channel: any): any {
    const newChannel = { ...channel };
    newChannel.members = newChannel.members.map((member: any) => member._id);
    newChannel.lastMessage = newChannel.lastMessage._id;
    return newChannel;
  }

  setUser() {
    this.profile = this.storageService.getUser().profile;
    this.user = this.storageService.getUser();
  }

  disconnectSocket() {
    this.removeAllListeners();
  }

  private removeAllListeners(): void {
    if (!this.socket) {
      return;
    }

    this.socket.off('inChannelMessage');
    this.socket.off('inSendboxMessage');
    this.socket.off('inGlobalMessage');
    this.socket.off('seenByAll');
    this.socket.off('inSendboxNewChannel');
    this.socket.off('inGlobalNewChannel');
    this.socket.off('inSendboxRemovedOfChannel');
    this.socket.off('inChannelRemovedOfChannel');
    this.socket.off('inSendboxNewInChannel');
    this.socket.off('inChannelNewChannel');
    this.socket.off('notSeenMessage');
  }

  disconnectFromSocket() {
    this.disconnectToSandbox();
    this.disconnectSocket();
  }

  clear(): void {
    this.disconnectFromSocket();
    this.profile = undefined;
    this.user = undefined;
    this.hospitalId = undefined;

    this.messagesSubject.complete();
    this.messagesSubject = new BehaviorSubject<any>([]);
    this.messages = this.messagesSubject.asObservable();

    this.channelsSubject.complete();
    this.channelsSubject = new BehaviorSubject<any>([]);
    this.channels = this.channelsSubject.asObservable();

    this.channelSubject.complete();
    this.channelSubject = new BehaviorSubject<any>({});
    this.channel = this.channelSubject.asObservable();

    this.newInChannelSubject.complete();
    this.newInChannelSubject = new Subject<any>();

    this.newInSendBoxSubject.complete();
    this.newInSendBoxSubject = new Subject<any>();

    this.removedChannelSubject.complete();
    this.removedChannelSubject = new Subject<any>();

    this.removedChannelInChannelSubject.complete();
    this.removedChannelInChannelSubject = new Subject<any>();

    this.unseenMessagesSubject.complete();
    this.unseenMessagesSubject = new Subject<any>();

    this.seenByAllMessageSubject.complete();
    this.seenByAllMessageSubject = new Subject<any>();

    this.isMessagingModeSubject.complete();
    this.isMessagingModeSubject = new BehaviorSubject<any>(false);

    this.messageSeenNumSubject.complete();
    this.messageSeenNumSubject = new BehaviorSubject<any>(0);

    this.inSendboxNewInChannelSubject.complete();
    this.inSendboxNewInChannelSubject = new BehaviorSubject<any>({});

    this.inSendboxNewChannelSubject.complete();
    this.inSendboxNewChannelSubject = new BehaviorSubject<any>({});

    this.inGlobalNewChannelSubject.complete();
    this.inGlobalNewChannelSubject = new Subject<any>();

    this.globalNewMessagesSubject.complete();
    this.globalNewMessagesSubject = new Subject<any>();
  }

  ngOnDestroy() {
    this.disconnectFromSocket();
  }
}
